import { useEffect, useMemo } from 'react';
import { parseAsString, useQueryState } from 'nuqs';
import {
  addWeekday,
  dayBoundsIso,
  parseDayIso,
  toIsoDate,
  toWeekday,
} from '../../../shared/lib/schedule';

/**
 * The board's day, shared by the grid and the navigation.
 *
 * Passed around as one object so the page can own the nav — rendered into `PageLayout`'s
 * `headerChildren` — while the widget owns the grid, without either calling the hook twice.
 */
export interface DispositionDay {
  dayIso: string;
  /** Start of the local day, for `GET /schedule`. */
  from: string;
  to: string;
  /** The board is showing the real calendar today — draw the current-time line. */
  isToday: boolean;
  /** `Heute` would move the board somewhere else. False on a weekend, when it already went there. */
  canGoToToday: boolean;
  prev: () => void;
  next: () => void;
  goToToday: () => void;
  /** Jump to a day derived from something else — e.g. back to the Termin being rescheduled. */
  goToDay: (dayIso: string) => void;
}

/**
 * The day the board is showing, persisted in the URL as `?day=YYYY-MM-DD`.
 *
 * **Weekdays only.** Saturday and Sunday are skipped by the arrows and a weekend `?day=` rewrites
 * itself to the following Monday, so the board never renders a day nobody works. `isToday` still
 * means the real calendar today, so the current-time line does not appear on Monday's board while
 * it is Saturday.
 *
 * Same shape as `worker-calendar`'s `useWorkerCalendarDays`, but this must not import it —
 * widget → widget is an FSD violation. The date helpers it needs live in `shared/lib/schedule`.
 */
export function useDispositionDay(): DispositionDay {
  const todayIso = toIsoDate(new Date());

  const [rawDay, setDay] = useQueryState('day', parseAsString.withDefault(todayIso));

  // A hand-edited or stale `?day=` must not blank the board, and a weekend one moves to Monday.
  const dayIso = useMemo(
    () => toWeekday(parseDayIso(rawDay) ? rawDay : todayIso),
    [rawDay, todayIso],
  );

  // Write the correction back, so the URL always says which day is actually on screen — a pasted
  // Saturday link would otherwise keep claiming Saturday while Monday is drawn.
  useEffect(() => {
    if (rawDay !== dayIso) setDay(dayIso);
  }, [rawDay, dayIso, setDay]);

  const { from, to } = useMemo(() => dayBoundsIso(dayIso), [dayIso]);

  return {
    dayIso,
    from,
    to,
    isToday: dayIso === todayIso,
    canGoToToday: dayIso !== toWeekday(todayIso),
    prev: () => setDay(addWeekday(dayIso, -1)),
    next: () => setDay(addWeekday(dayIso, 1)),
    goToToday: () => setDay(toWeekday(todayIso)),
    goToDay: (next: string) => setDay(toWeekday(next)),
  };
}
